import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Breadcrumb from '../../../components/feature/Breadcrumb';

interface DealRadarHeroProps {
  onAlertClick: () => void;
}

// Simulate a "spots claimed this week" count that feels live
function useSpotsClaimed() {
  const [count, setCount] = useState(47);
  useEffect(() => {
    // Slowly tick up to feel alive
    const id = setInterval(() => {
      setCount(prev => {
        const bump = Math.random() > 0.7 ? 1 : 0;
        return Math.min(prev + bump, 99);
      });
    }, 18000);
    return () => clearInterval(id);
  }, []);
  return count;
}

export default function DealRadarHero({ onAlertClick }: DealRadarHeroProps) {
  const spotsClaimed = useSpotsClaimed();

  return (
    <section className="relative bg-gradient-to-br from-[#0f1a10] via-[#1C2B1E] to-[#0f1a10] text-white pt-16 lg:pt-20 pb-24 overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/3f60e3c42be4e9e8583d55b209814892.jpg')] bg-cover bg-center opacity-5"></div>

      <div className="container mx-auto px-4 relative z-10">
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: 'First Dibs' },
          ]}
        />

        <div className="max-w-4xl mx-auto text-center mt-10">
          {/* Live badge with static crown icon */}
          <div className="inline-flex items-center gap-2 bg-[#E8B84B]/10 border border-[#E8B84B]/30 rounded-full px-5 py-2 mb-8">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-vip-crown-fill text-[#E8B84B] text-sm"></i>
            </div>
            <span className="text-sm font-semibold text-[#E8B84B] tracking-wide uppercase">Live Inventory — Updating Every 30 Seconds</span>
          </div>

          {/* Eyebrow */}
          <p className="text-[#E8B84B] font-bold text-sm uppercase tracking-widest mb-4">
            The best cars are gone before most people even look
          </p>

          {/* Headline */}
          <h1 className="font-bold text-5xl md:text-6xl lg:text-7xl mb-6 leading-[1.05] tracking-tight">
            Stop Losing Cars{' '}
            <br />
            <span className="relative inline-block">
              <span className="text-[#E8B84B]">You Actually Wanted.</span>
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#E8B84B]/40 rounded-full"></span>
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl text-white/80 mb-6 leading-relaxed max-w-2xl mx-auto">
            First Dibs members get notified the moment a matching vehicle hits our lot —
            <strong className="text-white"> before it's cleaned, photographed, or posted anywhere.</strong>
          </p>

          {/* Urgency indicator */}
          <div className="inline-flex items-center gap-3 bg-white/5 border border-[#E8B84B]/20 rounded-full px-5 py-2.5 mb-10">
            <div className="flex -space-x-1.5">
              {['bg-emerald-400', 'bg-sky-400', 'bg-rose-400', 'bg-amber-400'].map((c, i) => (
                <div key={i} className={`w-5 h-5 rounded-full ${c} border-2 border-[#1C2B1E]`}></div>
              ))}
            </div>
            <span className="text-sm text-white/80">
              <strong className="text-[#E8B84B]">{spotsClaimed} people</strong> claimed their spot this week
            </span>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-star-fill text-[#E8B84B] text-xs"></i>
            </div>
          </div>

          {/* 3 Value Props */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-12">
            {[
              {
                icon: 'ri-equalizer-line',
                title: 'Scarily Precise Filters',
                text: 'Budget, make, model, mileage, body type — you set the rules.',
              },
              {
                icon: 'ri-notification-3-line',
                title: 'Minutes, Not Days',
                text: 'SMS or email the moment a match lands. Not hours later. Minutes.',
              },
              {
                icon: 'ri-vip-crown-line',
                title: '24–48 Hour Head Start',
                text: 'You see it before it goes live. That\'s the whole point.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-5 hover:bg-white/10 transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8B84B]/15 rounded-full">
                  <i className={`${item.icon} text-xl text-[#E8B84B]`}></i>
                </div>
                <p className="text-sm font-bold text-white">{item.title}</p>
                <p className="text-xs text-white/60 leading-snug text-center">{item.text}</p>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={onAlertClick}
              className="inline-flex items-center gap-2 bg-[#E8B84B] text-[#0f1a10] px-8 py-4 rounded-lg font-bold text-base hover:bg-[#D4A43A] transition-all hover:scale-105 shadow-lg shadow-[#E8B84B]/20 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-vip-crown-line text-xl"></i>
              Claim My First Dibs Spot
            </button>
            <Link
              to="/inventory"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors whitespace-nowrap"
            >
              <i className="ri-car-line text-xl"></i>
              Browse What's Here Now
            </Link>
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap justify-center gap-8 mt-14 pt-10 border-t border-white/10">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <i className="ri-time-line text-[#E8B84B] text-lg"></i>
              <span>Alerts within minutes — not hours</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/70">
              <i className="ri-price-tag-3-line text-[#E8B84B] text-lg"></i>
              <span>Price drops posted the moment they happen</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/70">
              <i className="ri-spam-2-line text-[#E8B84B] text-lg"></i>
              <span>Zero spam. Unsubscribe anytime.</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}